import { AlertTriangle, Monitor, Printer } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { OptionCards, type OptionCard } from './OptionCards'
import type { KitchenFormState, KitchenModeChoice } from './ticketLife'

const MODE_OPTIONS: OptionCard<KitchenModeChoice>[] = [
  {
    value: 'kds',
    title: 'Kitchen display (KDS)',
    icon: Monitor,
    description:
      'Tickets show on the kitchen screen and cooks bump them when ready. A station set to Printer still prints its slip.',
  },
  {
    value: 'kot_only',
    title: 'Printed KOTs only',
    icon: Printer,
    description:
      'Every station prints a slip when the order is fired. The Kitchen Display is hidden from staff.',
  },
]

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** What the chosen mode does to the stations this venue already has. */
function modeImpact(
  mode: KitchenModeChoice,
  screens: number,
  printers: number,
): { warn: boolean; text: string } {
  if (mode === 'kot_only') {
    if (screens > 0) {
      return {
        warn: true,
        text: `${plural(screens, 'screen station')} will print slips instead while this is on.`,
      }
    }
    return { warn: false, text: 'All your stations already print.' }
  }
  if (screens === 0) {
    return {
      warn: true,
      text: 'No station is set to Kitchen screen, so nothing will show on the display. Add or edit a station below.',
    }
  }
  return {
    warn: false,
    text:
      printers > 0
        ? `${plural(screens, 'station')} on the screen, ${printers} printing slips.`
        : `${plural(screens, 'station')} on the screen.`,
  }
}

export function KitchenModePanel({
  form,
  onChange,
  screenStationCount,
  printerStationCount,
}: {
  form: KitchenFormState
  onChange: (patch: Partial<KitchenFormState>) => void
  screenStationCount: number
  printerStationCount: number
}) {
  const impact = modeImpact(form.mode, screenStationCount, printerStationCount)
  return (
    <section aria-labelledby="kitchen-mode-heading" className="space-y-4">
      <div>
        <h3 id="kitchen-mode-heading" className="text-lg font-semibold tracking-tight">
          How tickets reach the kitchen
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          One choice for the whole restaurant. Stations follow it.
        </p>
      </div>

      <OptionCards
        name="kitchen-mode"
        label="How tickets reach the kitchen"
        value={form.mode}
        onChange={(mode) => onChange({ mode })}
        options={MODE_OPTIONS}
        className="sm:grid-cols-2"
      />

      {impact.warn ? (
        <p className="flex items-start gap-2 rounded-lg border border-warning-border bg-warning-surface px-3 py-2.5 text-sm text-warning-fg">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{impact.text}</span>
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">{impact.text}</p>
      )}

      <div className="flex items-start justify-between gap-6 rounded-lg border border-border bg-card px-4 py-3.5">
        <div className="min-w-0">
          <label htmlFor="confirm-kot-print" className="text-sm font-medium">
            Ask before printing each KOT
          </label>
          <p className="mt-0.5 max-w-prose text-sm text-muted-foreground">
            Fire KOT asks Print or Skip, for counters that start making the order without a slip.
            When off, slips print straight away.
          </p>
        </div>
        <Switch
          id="confirm-kot-print"
          checked={form.confirmKotPrint}
          onCheckedChange={(confirmKotPrint) => onChange({ confirmKotPrint })}
        />
      </div>
    </section>
  )
}
